import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AppUser, LoginCredentials, AuthContextType } from '../types/admin';
import { login } from '../services/authService';

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
	children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [user, setUser] = useState<AppUser | null>(null);
	const [token, setToken] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		// Check for stored token on app start
		const storedToken = localStorage.getItem('auth_token');
		const storedUser = localStorage.getItem('auth_user');

		if (storedToken && storedUser) {
			try {
				const parsedUser = JSON.parse(storedUser);
				setToken(storedToken);
				setUser(parsedUser);
			} catch (error) {
				// Clear invalid stored data
				localStorage.removeItem('auth_token');
				localStorage.removeItem('auth_user');
			}
		}
		setIsLoading(false);
	}, []);

	const loginUser = async (credentials: LoginCredentials) => {
		try {
			const response = await login(credentials);

			setToken(response.token);
			setUser(response.user);

			// Store in localStorage
			localStorage.setItem('auth_token', response.token);
			localStorage.setItem('auth_user', JSON.stringify(response.user));
		} catch (error) {
			throw error;
		}
	};

	const logout = () => {
		setToken(null);
		setUser(null);
		localStorage.removeItem('auth_token');
		localStorage.removeItem('auth_user');
	};

	// Helper function to check if user is admin
	const isAdmin = user ? 'role' in user && (user.role === 'admin' || user.role === 'super-admin') : false;

	const value: AuthContextType = {
		user,
		token,
		login: loginUser,
		logout,
		isLoading,
		isAuthenticated: !!token && !!user,
		isAdmin,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};
